'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface ImageModalProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageModal({
  src,
  alt,
  isOpen,
  onClose,
}: ImageModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8 transition-all duration-500',
        isOpen
          ? 'visible opacity-100'
          : 'invisible opacity-0 pointer-events-none',
      )}
    >
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-xl"
        onClick={onClose}
      />

      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md transition-all z-20 cursor-pointer shadow-lg active:scale-95"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      <div
        className={cn(
          'relative w-full max-w-2xl aspect-square rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl transition-all duration-500 transform',
          isOpen ? 'scale-100 translate-y-0' : 'scale-90 translate-y-12',
        )}
      >
        <Image
          src={src}
          alt={alt}
          fill
          sizes="(min-width: 640px) 672px, 100vw"
          className="object-cover"
          priority
          unoptimized
        />
      </div>
    </div>,
    document.body,
  );
}
